import { actionCreators as MaintenanceActionCreator } from "./actionCreator";
import { MAINTENANCE_SAVE_BLOCKED_MESSAGE } from "./messages";
import { MonthEndLockedAction } from "./models";
import { FeatureToggleValue, KnownFeatureToggles } from "modules/features/models";
import { Log } from "modules/logging/log";
import { NotificationStyle } from "modules/notifications/models";
import { ApplicationState } from "store";

/**
 * Checks - with a live call, not a cached value - whether the month-end job is running, and if it
 * is, tells the member their changes were not saved.
 *
 * Call this at the top of any save thunk that must not run during month end. It is deliberately a
 * submit-time check rather than a disabled button: the status can change while a form is open, and
 * a member who has already typed something deserves an explanation and their data back, not a dead
 * button.
 *
 * @param action what the member was trying to save, for telemetry.
 * @returns true if the save should be abandoned.
 */
export async function isSaveBlockedByMonthEndMaintenance(
    dispatch: any,
    getState: () => ApplicationState,
    action: MonthEndLockedAction
): Promise<boolean> {
    const toggle = getState()?.features?.featureToggles?.[KnownFeatureToggles.EnableMonthEndMaintenanceLockout];

    // Skip the round trip entirely when the feature is off.
    if (toggle?.toggleValue !== FeatureToggleValue.On) {
        return false;
    }

    const monthEndStatus = await dispatch(MaintenanceActionCreator.getMonthEndStatus());

    if (!monthEndStatus?.isInProgress) {
        return false;
    }

    // The alert being on screen only tells us a member saw it. This tells us it cost them something:
    // they filled a form in, submitted it, and were turned away.
    //
    // Emulated sessions are excluded for the same reason as the sightings in MonthEndMaintenanceAlert
    // - staff are not members, and they are most likely to be here during a month end precisely
    // because a member has already complained. Only the reporting is skipped: the save is still
    // refused below, and emulation has its own read-only rules that do not depend on this.
    if (!getState()?.auth?.session?.emulatedSession) {
        Log.monthEndLockoutBlockedSave(action);
    }

    dispatch({
        type: "ADD_NOTIFICATION",
        message: MAINTENANCE_SAVE_BLOCKED_MESSAGE,
        style: NotificationStyle.Error,
        autohide: false,
        canDismiss: true
    });

    return true;
}
